import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { useI18n } from '../../contexts/I18nContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Flame,
  Volume2,
  Check,
  X,
  ArrowRight,
  Loader2,
  Mic,
  Trophy,
  Star,
  Sparkles,
  ChevronLeft,
  Hammer,
  Target,
  Zap,
  Clock,
  Award,
  Keyboard,
  ChevronDown,
  ChevronUp,
  MessageSquarePlus
} from 'lucide-react';
import AIQuestionHelper from '../../components/AIQuestionHelper';
import GrammarRecommendations from '../../components/GrammarRecommendations';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

// Special Characters Keyboard Component
const SpecialCharactersKeyboard = ({ onCharacterClick, isOpen, onToggle }) => {
  const [activeCategory, setActiveCategory] = useState('nordic');
  
  const characterSets = {
    nordic: {
      label: 'Nordic',
      chars: ['æ', 'ø', 'å', 'ä', 'ö', 'Æ', 'Ø', 'Å', 'Ä', 'Ö']
    },
    german: {
      label: 'German',
      chars: ['ä', 'ö', 'ü', 'ß', 'Ä', 'Ö', 'Ü']
    },
    french: {
      label: 'French',
      chars: ['é', 'è', 'ê', 'ë', 'à', 'â', 'ç', 'ù', 'û', 'ô', 'î', 'ï', 'œ', 'É', 'È', 'Ê', 'Ç', 'Œ']
    },
    spanish: {
      label: 'Spanish',
      chars: ['á', 'é', 'í', 'ó', 'ú', 'ü', 'ñ', '¿', '¡', 'Á', 'É', 'Í', 'Ó', 'Ú', 'Ñ']
    },
    portuguese: {
      label: 'Portuguese',
      chars: ['ã', 'õ', 'á', 'é', 'í', 'ó', 'ú', 'â', 'ê', 'ô', 'ç', 'Ã', 'Õ', 'Ç']
    },
    italian: {
      label: 'Italian',
      chars: ['à', 'è', 'é', 'ì', 'ò', 'ù', 'À', 'È', 'É', 'Ì', 'Ò', 'Ù']
    },
    polish: {
      label: 'Polish',
      chars: ['ą', 'ć', 'ę', 'ł', 'ń', 'ó', 'ś', 'ź', 'ż', 'Ą', 'Ć', 'Ę', 'Ł', 'Ń', 'Ó', 'Ś', 'Ź', 'Ż']
    },
    czech: {
      label: 'Czech/Slovak',
      chars: ['á', 'č', 'ď', 'é', 'ě', 'í', 'ň', 'ó', 'ř', 'š', 'ť', 'ú', 'ů', 'ý', 'ž', 'Á', 'Č', 'Ď', 'É', 'Š', 'Ž']
    },
    igbo: {
      label: 'Igbo',
      chars: ['ị', 'ọ', 'ụ', 'ṅ', 'Ị', 'Ọ', 'Ụ', 'Ṅ', 'à', 'è', 'ì', 'ò', 'ù', 'á', 'é', 'í', 'ó', 'ú']
    },
    other: {
      label: 'Other',
      chars: ['ð', 'þ', 'ý', 'ÿ', 'ā', 'ē', 'ī', 'ō', 'ū', 'ğ', 'ş', 'ı', 'Ğ', 'Ş', 'İ', '€', '£', '«', '»']
    }
  };

  return (
    <div className="relative">
      {/* Toggle Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={onToggle}
        className={`flex items-center gap-2 ${isOpen ? 'bg-amber-500/20 border-amber-500' : 'border-slate-600'}`}
      >
        <Keyboard className="h-4 w-4" />
        <span className="text-sm">ÆØÅ</span>
        {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </Button>
      
      {/* Keyboard Panel */}
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 z-50 w-80 md:w-96 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3 animate-fadeIn">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-1 mb-3 pb-2 border-b border-slate-700">
            {Object.entries(characterSets).map(([key, { label }]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  activeCategory === key
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          
          {/* Character Grid */}
          <div className="flex flex-wrap gap-1">
            {characterSets[activeCategory].chars.map((char, idx) => (
              <button
                key={idx}
                onClick={() => onCharacterClick(char)}
                className="w-9 h-9 flex items-center justify-center text-lg font-medium bg-slate-700 hover:bg-amber-500 hover:text-white rounded transition-colors text-slate-200"
              >
                {char}
              </button>
            ))}
          </div>
          
          {/* Hint */}
          <p className="text-xs text-slate-500 mt-2 text-center">
            Click a character to insert it
          </p>
        </div>
      )}
    </div>
  );
};

// Level colors
const LEVEL_COLORS = {
  iron: { 
    bg: 'bg-gradient-to-r from-slate-600 to-slate-700', 
    text: 'text-slate-200', 
    border: 'border-slate-500',
    accent: 'text-slate-300'
  },
  bronze: { 
    bg: 'bg-gradient-to-r from-amber-700 to-amber-800', 
    text: 'text-amber-100', 
    border: 'border-amber-600',
    accent: 'text-amber-300'
  },
  silver: { 
    bg: 'bg-gradient-to-r from-slate-300 to-slate-400', 
    text: 'text-slate-800', 
    border: 'border-slate-300',
    accent: 'text-slate-600'
  },
  gold: { 
    bg: 'bg-gradient-to-r from-yellow-400 to-amber-500', 
    text: 'text-yellow-900', 
    border: 'border-yellow-400',
    accent: 'text-yellow-800'
  },
};

// Blacksmith reactions
const SMITH_REACTIONS = {
  idle: 'Ready to forge!',
  correct: ['Nice strike!', 'Well done!', 'Perfect!', 'Keep it up!', 'Excellent!'],
  wrong: ['Try again!', 'Almost!', 'Keep forging!', 'Don\'t give up!'],
  streak: ['On fire!', 'Unstoppable!', 'Master smith!'],
};

export default function ForgeLesson() {
  const navigate = useNavigate();
  const { languageCode, course, lesson } = useParams();
  const { t } = useI18n();
  
  // Helper function to normalize text - removes diacritics and punctuation
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      // Remove diacritics (accents)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Remove punctuation
      .replace(/[.,!?;:'"¿¡«»""''„‚—–\-()[\]{}]/g, '')
      // Normalize whitespace
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  // Helper to compare answers ignoring diacritics and punctuation
  const answersMatch = (userAnswer, correctAnswer) => {
    return normalizeText(userAnswer) === normalizeText(correctAnswer);
  };
  
  const [loading, setLoading] = useState(true);
  const [lessonData, setLessonData] = useState(null);
  const [currentLevel, setCurrentLevel] = useState('iron');
  const [currentStep, setCurrentStep] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [stepScore, setStepScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [typedAnswer, setTypedAnswer] = useState('');
  const [dragItems, setDragItems] = useState([]);
  const [levelComplete, setLevelComplete] = useState(false);
  const [lessonComplete, setLessonComplete] = useState(false);
  const [rankUp, setRankUp] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [streak, setStreak] = useState(0);
  
  // Mistake tracking for retry
  const [mistakes, setMistakes] = useState([]); // Array of question IDs that were answered wrong
  const [retryPhase, setRetryPhase] = useState(false); // Are we in retry phase?
  const [retryQuestions, setRetryQuestions] = useState([]); // Questions to retry
  const [retryIndex, setRetryIndex] = useState(0);
  
  // Question attempt tracking for scoring
  const [questionAttempts, setQuestionAttempts] = useState({}); // {question_id: attempt_count}
  
  // Step completion stats
  const [showStepStats, setShowStepStats] = useState(false);
  const [stepStats, setStepStats] = useState({
    correct: 0,
    wrong: 0,
    score: 0,
    perfectBonus: 0,
    totalTime: 0
  });
  const [stepStartTime, setStepStartTime] = useState(null);
  
  // Blacksmith state
  const [blacksmith, setBlacksmith] = useState(null);
  const [smithReaction, setSmithReaction] = useState('idle');
  const [smithMessage, setSmithMessage] = useState(SMITH_REACTIONS.idle);
  const [smithAnimating, setSmithAnimating] = useState(false);
  
  // Special characters keyboard
  const [showSpecialKeyboard, setShowSpecialKeyboard] = useState(false);
  const typingInputRef = useRef(null);
  
  // Suggestion dialog state
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [suggestionText, setSuggestionText] = useState('');
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);
  
  // Speech recognition state
  const [isRecording, setIsRecording] = useState(false);
  const [spokenText, setSpokenText] = useState('');
  const [speechError, setSpeechError] = useState(null);
  const recognitionRef = useRef(null);
  
  // Sentence Preview Phase - show all sentences before questions
  const [previewPhase, setPreviewPhase] = useState(true); // Start in preview phase
  const [previewSentences, setPreviewSentences] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [showTranslation, setShowTranslation] = useState(false);
  const [previewAudioPlayed, setPreviewAudioPlayed] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const audioRef = useRef(null);

  // Global Enter key handler - continue after feedback or advance preview
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        // If showing feedback, continue to next question
        if (showFeedback) {
          e.preventDefault();
          nextQuestion();
        }
        // If in preview phase and translation is shown, advance to next sentence
        else if (previewPhase && showTranslation) {
          e.preventDefault();
          nextPreviewSentence();
        }
      }
      // Space bar to reveal translation in preview
      if (e.key === ' ' && previewPhase && !showTranslation) {
        e.preventDefault();
        setShowTranslation(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFeedback, previewPhase, showTranslation]);

  useEffect(() => {
    fetchLessonData();
    fetchBlacksmithData();
  }, [languageCode, course, lesson]);

  // Fetch preview sentences when level changes
  useEffect(() => {
    if (lessonData && currentStep === 1) {
      fetchPreviewSentences();
    }
  }, [lessonData, currentLevel]);

  useEffect(() => {
    if (lessonData) {
      fetchQuestions();
    }
  }, [lessonData, currentLevel, currentStep]);

  const fetchLessonData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/forge/lesson/${languageCode}/${course}/${lesson}`);
      setLessonData(response.data);
      
      if (response.data.progress) {
        setCurrentLevel(response.data.progress.current_level || 'iron');
        setCurrentStep(response.data.progress.current_step || 1);
      }
    } catch (err) {
      console.error('Failed to fetch lesson:', err);
      toast.error(err.response?.data?.detail || 'Failed to load lesson');
      navigate(`/forge/${languageCode}?course=${course}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlacksmithData = async () => {
    try {
      const response = await api.get(`/forge/progress/${languageCode}`);
      setBlacksmith(response.data);
    } catch (err) {
      console.error('Failed to fetch blacksmith:', err);
    }
  };

  // Fetch preview sentences for the current level
  const fetchPreviewSentences = async () => {
    if (currentStep !== 1) {
      setPreviewPhase(false);
      return;
    }
    
    setLoadingPreview(true);
    try {
      const response = await api.get(
        `/forge/preview/${languageCode}/${course}/${lesson}/${currentLevel}`
      );
      const sentences = response.data.sentences || [];
      
      if (sentences.length > 0) {
        setPreviewSentences(sentences);
        setCurrentPreviewIndex(0);
        setShowTranslation(false);
        setPreviewAudioPlayed(false);
        setPreviewPhase(true);
      } else {
        // No sentences found, skip preview
        setPreviewPhase(false);
      }
    } catch (err) {
      console.error('Failed to fetch preview sentences:', err);
      setPreviewPhase(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await api.get(
        `/forge/level/${languageCode}/${course}/${lesson}/${currentLevel}?step=${currentStep}`
      );
      const questionsData = response.data.questions || [];
      setQuestions(questionsData);
      setCurrentQuestionIndex(0);
      setStepScore(0);
      setStartTime(Date.now());
      setStepStartTime(Date.now());
      setMistakes([]);
      setRetryPhase(false);
      setRetryQuestions([]);
      setRetryIndex(0);
      setQuestionAttempts({});
      
      // For steps 2-4, skip preview (already done in step 1)
      if (currentStep !== 1) {
        setPreviewPhase(false);
      }
      
      if (questionsData[0]?.question_type === 'drag_drop') {
        const items = questionsData[0].options || [];
        setDragItems(items.map(word => ({ word, selected: false })));
      }
    } catch (err) {
      console.error('Failed to fetch questions:', err);
      setPreviewPhase(false);
    }
  };

  // Sound effect URLs
  const SOUND_ANVIL = 'https://customer-assets.emergentagent.com/job_02091aa1-8cc5-43f2-b8da-ef986818666e/artifacts/5nshptwg_anvil.mp3';
  const SOUND_BREAK = 'https://customer-assets.emergentagent.com/job_02091aa1-8cc5-43f2-b8da-ef986818666e/artifacts/x6t994ac_break.mp3';

  const playSound = (soundType) => {
    try {
      let audioUrl;
      if (soundType === 'anvil_strike' || soundType === 'correct') {
        audioUrl = SOUND_ANVIL;
      } else {
        audioUrl = SOUND_BREAK;
      }
      
      const audio = new Audio(audioUrl);
      audio.volume = 0.5;
      audio.play().catch(err => {
        console.log('Audio playback failed, falling back to synthesized sound:', err);
        playSynthesizedSound(soundType);
      });
    } catch (e) {
      console.log('Audio not supported');
      playSynthesizedSound(soundType);
    }
  };

  const playSynthesizedSound = (soundType) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (soundType === 'anvil_strike' || soundType === 'correct') {
        oscillator.frequency.value = 1200;
        oscillator.type = 'triangle';
        gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.15);
      } else {
        oscillator.frequency.value = 150;
        oscillator.type = 'sawtooth';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.4);
      }
    } catch (e) {
      console.log('Synthesized audio not supported');
    }
  };

  const playWordAudio = async (wordId, wordText = null) => {
    try {
      const audioUrl = `${process.env.REACT_APP_BACKEND_URL}/api/forge-audio/no/${wordId}.mp3`;
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      return new Promise((resolve) => {
        audio.onended = () => resolve();
        audio.onerror = () => {
          // Fallback to speech synthesis
          if ('speechSynthesis' in window && wordText) {
            const utterance = new SpeechSynthesisUtterance(wordText);
            utterance.lang = 'nb-NO';
            utterance.rate = 0.8;
            utterance.onend = () => resolve();
            window.speechSynthesis.speak(utterance);
          } else {
            resolve();
          }
        };
        audio.play().catch(() => {
          if ('speechSynthesis' in window && wordText) {
            const utterance = new SpeechSynthesisUtterance(wordText);
            utterance.lang = 'nb-NO';
            utterance.rate = 0.8;
            utterance.onend = () => resolve();
            window.speechSynthesis.speak(utterance);
          } else {
            resolve();
          }
        });
      });
    } catch (e) {
      console.log('Failed to play audio:', e);
    }
  };

  // Preview Phase Functions - Now shows SENTENCES
  const playPreviewAudio = async () => {
    const currentSentence = previewSentences[currentPreviewIndex];
    setPreviewAudioPlayed(true);
    
    // Always show translation after a delay, even if audio fails
    const translationTimer = setTimeout(() => setShowTranslation(true), 2500);
    
    if (!currentSentence?.target) return;
    
    // First try to play from database audio
    if (currentSentence.sentence_id && currentSentence.has_audio) {
      try {
        const audioUrl = `${process.env.REACT_APP_BACKEND_URL}/api/sentences/${currentSentence.sentence_id}/audio/${languageCode}`;
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onended = () => {
          clearTimeout(translationTimer);
          setTimeout(() => setShowTranslation(true), 500);
        };
        audio.onerror = () => {
          // Fallback to speech synthesis
          playSpeechSynthesis(currentSentence.target, translationTimer);
        };
        await audio.play();
        return;
      } catch (e) {
        console.log('Database audio failed, using speech synthesis');
      }
    }
    
    // Fallback to speech synthesis
    playSpeechSynthesis(currentSentence.target, translationTimer);
  };
  
  // Helper for speech synthesis fallback
  const playSpeechSynthesis = (text, translationTimer) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      // Map language codes to speech synthesis language tags
      const langMap = {
        'ru': 'ru-RU',
        'zh': 'zh-CN',
        'fr': 'fr-FR',
        'de': 'de-DE',
        'es': 'es-ES',
        'no': 'nb-NO',
        'ja': 'ja-JP',
        'ko': 'ko-KR',
        'ar': 'ar-SA',
        'fa': 'fa-IR',
      };
      utterance.lang = langMap[languageCode] || languageCode;
      utterance.rate = 0.85;
      utterance.onend = () => {
        clearTimeout(translationTimer);
        setTimeout(() => setShowTranslation(true), 500);
      };
      utterance.onerror = () => {
        clearTimeout(translationTimer);
        setShowTranslation(true);
      };
      window.speechSynthesis.speak(utterance);
    } else {
      // No speech synthesis, just show translation
      clearTimeout(translationTimer);
      setTimeout(() => setShowTranslation(true), 1000);
    }
  };

  const nextPreviewSentence = () => {
    if (currentPreviewIndex < previewSentences.length - 1) {
      setCurrentPreviewIndex(prev => prev + 1);
      setShowTranslation(false);
      setPreviewAudioPlayed(false);
    } else {
      // End preview phase, start questions
      setPreviewPhase(false);
      setStartTime(Date.now());
    }
  };

  const skipPreview = () => {
    setPreviewPhase(false);
    setStartTime(Date.now());
  };

  // Speech Recognition Setup and Functions
  const initSpeechRecognition = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setSpeechError('Speech recognition not supported in this browser');
      return null;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    // Map language codes to speech recognition language tags
    const langMap = {
      'ru': 'ru-RU',
      'zh': 'zh-CN',
      'fr': 'fr-FR',
      'de': 'de-DE',
      'es': 'es-ES',
      'no': 'nb-NO',
      'ja': 'ja-JP',
      'ko': 'ko-KR',
      'ar': 'ar-SA',
      'fa': 'fa-IR',
      'it': 'it-IT',
      'pt': 'pt-BR',
    };
    
    recognition.lang = langMap[languageCode] || languageCode;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 3;
    
    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript = transcript;
        }
      }
      
      if (finalTranscript) {
        setSpokenText(finalTranscript);
        setIsRecording(false);
        // Auto-submit the answer after a short delay
        setTimeout(() => {
          submitAnswer(finalTranscript);
        }, 500);
      } else if (interimTranscript) {
        setSpokenText(interimTranscript);
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
      if (event.error === 'no-speech') {
        setSpeechError('No speech detected. Try again.');
      } else if (event.error === 'audio-capture') {
        setSpeechError('Microphone not available.');
      } else {
        setSpeechError(`Error: ${event.error}`);
      }
    };
    
    recognition.onend = () => {
      setIsRecording(false);
    };
    
    return recognition;
  }, [languageCode]);

  const startSpeechRecognition = () => {
    setSpeechError(null);
    setSpokenText('');
    
    if (!recognitionRef.current) {
      recognitionRef.current = initSpeechRecognition();
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (e) {
        console.error('Failed to start speech recognition:', e);
        setSpeechError('Failed to start recording');
      }
    }
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
  };

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Text similarity checker for speaking questions
  const calculateSimilarity = (str1, str2) => {
    // Normalize strings: lowercase, remove punctuation, trim
    const normalize = (s) => s.toLowerCase().replace(/[.,!?;:'"]/g, '').trim();
    const a = normalize(str1);
    const b = normalize(str2);
    
    if (a === b) return 1.0;
    
    // Levenshtein distance for fuzzy matching
    const matrix = [];
    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    const maxLen = Math.max(a.length, b.length);
    return maxLen === 0 ? 1 : 1 - matrix[b.length][a.length] / maxLen;
  };

  // Handle special character insertion
  const handleSpecialCharacterClick = (char) => {
    setTypedAnswer(prev => prev + char);
    // Focus back on input
    if (typingInputRef.current) {
      typingInputRef.current.focus();
    }
  };

  // Auto-play audio when preview sentence changes
  useEffect(() => {
    if (previewPhase && previewSentences.length > 0 && !previewAudioPlayed) {
      const timer = setTimeout(() => {
        playPreviewAudio();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [currentPreviewIndex, previewPhase, previewSentences]);

  const animateSmith = (correct) => {
    setSmithAnimating(true);
    setSmithReaction(correct ? 'correct' : 'wrong');
    
    if (correct) {
      const newStreak = streak + 1;
      setStreak(newStreak);
      if (newStreak >= 5) {
        setSmithMessage(SMITH_REACTIONS.streak[Math.floor(Math.random() * SMITH_REACTIONS.streak.length)]);
      } else {
        setSmithMessage(SMITH_REACTIONS.correct[Math.floor(Math.random() * SMITH_REACTIONS.correct.length)]);
      }
    } else {
      setStreak(0);
      setSmithMessage(SMITH_REACTIONS.wrong[Math.floor(Math.random() * SMITH_REACTIONS.wrong.length)]);
    }
    
    setTimeout(() => {
      setSmithAnimating(false);
      setSmithReaction('idle');
      setSmithMessage(SMITH_REACTIONS.idle);
    }, 2000);
  };

  const submitAnswer = async (answer) => {
    if (showFeedback) return;
    
    const question = retryPhase ? retryQuestions[retryIndex] : questions[currentQuestionIndex];
    const responseTime = Date.now() - startTime;
    
    // Track attempts for this question
    const questionId = question.question_id;
    const currentAttempts = questionAttempts[questionId] || 0;
    const newAttempts = currentAttempts + 1;
    setQuestionAttempts(prev => ({ ...prev, [questionId]: newAttempts }));
    
    try {
      const response = await api.post('/forge/answer', {
        question_id: question.question_id,
        answer: answer,
        response_time_ms: responseTime
      });
      
      setIsCorrect(response.data.correct);
      setSelectedAnswer(answer);
      setShowFeedback(true);
      
      // Calculate score: 10 pts base, -2 per repeat
      if (response.data.correct) {
        const basePoints = 10;
        const repeatPenalty = (newAttempts - 1) * 2; // -2 for each repeat
        const points = Math.max(basePoints - repeatPenalty, 2); // Minimum 2 points
        setStepScore(prev => prev + points);
        setScore(prev => prev + points);
      } else {
        // Track mistake for retry phase
        if (!retryPhase && !mistakes.includes(questionId)) {
          setMistakes(prev => [...prev, questionId]);
        }
      }
      
      playSound(response.data.sound);
      animateSmith(response.data.correct);
      
    } catch (err) {
      console.error('Failed to submit answer:', err);
      toast.error('Failed to submit answer');
    }
  };

  const submitSuggestion = async () => {
    if (!suggestionText.trim()) {
      toast.error('Please enter your suggested answer');
      return;
    }
    
    setSubmittingSuggestion(true);
    try {
      const currentQ = retryPhase ? retryQuestions[retryIndex] : questions[currentQuestionIndex];

      const res = await api.post('/forge/suggest-answer', {
        question_id: currentQ?.question_id,
        suggested_answer: suggestionText.trim(),
        user_answer: typedAnswer || selectedAnswer || '',
        correct_answer: currentQ?.correct_answer,
        prompt: currentQ?.prompt,
        lesson_id: lesson,
        language_code: languageCode,
      }, { timeout: 20000 });

      if (res?.data?.success === false) {
        throw new Error(res?.data?.message || 'Backend returned success=false');
      }

      toast.success('Thank you! Your suggestion has been submitted for review.');
      setShowSuggestionDialog(false);
      setSuggestionText('');
    } catch (err) {
      // Surface the real reason so users/admins can report accurately.
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Unknown error';
      console.error('Failed to submit suggestion:', err?.response?.status, detail, err);
      toast.error(`Failed to submit suggestion: ${detail}`);
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  const nextQuestion = async () => {
    setShowFeedback(false);
    setSelectedAnswer(null);
    setTypedAnswer('');
    setIsCorrect(null);
    setStartTime(Date.now());
    
    if (retryPhase) {
      // In retry phase
      if (retryIndex < retryQuestions.length - 1) {
        const nextIdx = retryIndex + 1;
        setRetryIndex(nextIdx);
        
        const nextQ = retryQuestions[nextIdx];
        if (nextQ?.question_type === 'drag_drop') {
          const items = nextQ.options || [];
          setDragItems(items.map(word => ({ word, selected: false })));
        }
      } else {
        // Retry phase complete - now complete the step
        await showStepCompletion();
      }
    } else {
      // Normal phase
      if (currentQuestionIndex < questions.length - 1) {
        const nextIdx = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIdx);
        
        const nextQ = questions[nextIdx];
        if (nextQ?.question_type === 'drag_drop') {
          const items = nextQ.options || [];
          setDragItems(items.map(word => ({ word, selected: false })));
        }
      } else {
        // End of normal questions - check if we need retry phase
        if (mistakes.length > 0) {
          // Enter retry phase
          const retryQs = questions.filter(q => mistakes.includes(q.question_id));
          setRetryQuestions(retryQs);
          setRetryPhase(true);
          setRetryIndex(0);
          toast.info(`Let's review ${retryQs.length} question${retryQs.length > 1 ? 's' : ''} you missed!`);
          
          const firstRetryQ = retryQs[0];
          if (firstRetryQ?.question_type === 'drag_drop') {
            const items = firstRetryQ.options || [];
            setDragItems(items.map(word => ({ word, selected: false })));
          }
        } else {
          // No mistakes - complete step
          await showStepCompletion();
        }
      }
    }
  };

  const showStepCompletion = async () => {
    const totalTime = Math.round((Date.now() - stepStartTime) / 1000);
    const correctCount = questions.length - mistakes.length;
    const wrongCount = mistakes.length;
    const isPerfect = wrongCount === 0;
    const perfectBonus = isPerfect ? 20 : 0;
    
    // Add perfect bonus to score
    if (isPerfect) {
      setStepScore(prev => prev + 20);
      setScore(prev => prev + 20);
    }
    
    setStepStats({
      correct: correctCount,
      wrong: wrongCount,
      score: stepScore + perfectBonus,
      perfectBonus: perfectBonus,
      totalTime: totalTime
    });
    
    setShowStepStats(true);
  };

  const completeStep = async () => {
    setShowStepStats(false);
    
    try {
      const response = await api.post(
        `/forge/complete-step?language_code=${languageCode}&course=${course}&lesson=${lesson}&level=${currentLevel}&step=${currentStep}&score=${stepScore}`
      );
      
      if (response.data.level_complete) {
        await completeLevel();
      } else {
        // Go back to path after step completion
        navigate(`/forge/${languageCode}?course=${course}`);
      }
    } catch (err) {
      console.error('Failed to complete step:', err);
    }
  };

  const completeLevel = async () => {
    try {
      const response = await api.post(
        `/forge/complete-level?language_code=${languageCode}&course=${course}&lesson=${lesson}&level=${currentLevel}&total_score=${score}`
      );
      
      setLevelComplete(true);
      
      if (response.data.rank_up) {
        setRankUp({
          rank: response.data.new_rank,
          image: response.data.rank_image
        });
        fetchBlacksmithData();
      }
      
      if (response.data.lesson_complete) {
        setLessonComplete(true);
      }
      
      playSound('anvil_strike');
      
    } catch (err) {
      console.error('Failed to complete level:', err);
    }
  };

  const continueToNextLevel = () => {
    const levelOrder = ['iron', 'bronze', 'silver', 'gold'];
    const currentIndex = levelOrder.indexOf(currentLevel);
    
    if (currentIndex < 3) {
      setCurrentLevel(levelOrder[currentIndex + 1]);
      setCurrentStep(1);
      setScore(0);
      setLevelComplete(false);
      setRankUp(null);
      setPreviewPhase(true); // Reset to preview phase for new level
      setPreviewSentences([]);
      setCurrentPreviewIndex(0);
      setShowTranslation(false);
      setPreviewAudioPlayed(false);
    } else {
      navigate(`/forge/${languageCode}?course=${course}`);
    }
  };

  const goToNextLesson = () => {
    const nextLesson = parseInt(lesson) + 1;
    if (nextLesson <= 25) {
      navigate(`/forge/${languageCode}/lesson/${course}/${nextLesson}`);
      window.location.reload();
    } else {
      navigate(`/forge/${languageCode}?course=${course}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <Loader2 className="h-12 w-12 animate-spin text-amber-500" />
      </div>
    );
  }

  const currentQuestion = retryPhase ? retryQuestions[retryIndex] : questions[currentQuestionIndex];
  const levelColor = LEVEL_COLORS[currentLevel];
  const totalQuestions = retryPhase ? retryQuestions.length : questions.length;
  const currentIdx = retryPhase ? retryIndex : currentQuestionIndex;
  const progressPercent = ((currentIdx + (showFeedback ? 1 : 0)) / totalQuestions) * 100;

  // Sentence Preview Phase - Show all sentences before questions
  if (previewPhase && (previewSentences.length > 0 || loadingPreview)) {
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" data-testid="preview-phase">
        {/* Header */}
        <div className={`sticky top-0 z-10 ${levelColor.bg} ${levelColor.border} border-b p-3`}>
          <div className="flex items-center justify-between max-w-5xl mx-auto">
            <Button 
              variant="ghost" 
              size="sm"
              className={levelColor.text}
              onClick={() => navigate(`/forge/${languageCode}?course=${course}`)}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            
            <div className="text-center">
              <p className={`text-sm font-bold capitalize ${levelColor.text}`}>
                {t(`forge.${currentLevel}`)} {t('forge.levelLabel')} • {t('forge.sentencePreview')}
              </p>
              <p className={`text-xs ${levelColor.accent} opacity-80`}>
                {t('forge.lesson')} {lesson} • {lessonData?.theme}
              </p>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              className={levelColor.text}
              onClick={skipPreview}
            >
              {t('forge.skip')}
            </Button>
          </div>
          
          {/* Progress bar for preview */}
          <div className="max-w-5xl mx-auto mt-2">
            <Progress value={((currentPreviewIndex + 1) / Math.max(previewSentences.length, 1)) * 100} className="h-2" />
            <p className="text-center text-xs mt-1 opacity-75">
              {currentPreviewIndex + 1} / {previewSentences.length}
            </p>
          </div>
        </div>

        {/* Preview Card - Sentences */}
        <div className="max-w-3xl mx-auto p-6 flex flex-col items-center justify-center min-h-[70vh]">
          {loadingPreview ? (
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-400">{t('common.loading')}</p>
            </div>
          ) : previewSentences.length > 0 ? (
            <>
              <Card className="w-full bg-slate-800/80 border-slate-700 shadow-2xl">
                <CardContent className="pt-8 pb-8">
                  {/* Target Language Sentence */}
                  <div className="text-center mb-6">
                    <p className="text-sm text-amber-400 uppercase tracking-wider mb-4 flex items-center justify-center gap-2">
                      <span className="inline-block w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
                      {t('forge.learnThis')}
                    </p>
                    {previewSentences[currentPreviewIndex]?.tokens?.length > 0 ? (
                      <div className="flex flex-wrap justify-center gap-x-2 gap-y-3 mb-6">
                        {previewSentences[currentPreviewIndex].tokens.map((tok, i) => (
                          tok.is_word ? (
                            <span
                              key={i}
                              className="inline-flex flex-col items-center px-1.5 py-0.5 rounded hover:bg-amber-500/10 transition-colors cursor-default"
                              data-testid={`preview-token-${i}`}
                            >
                              {tok.pinyin && (
                                <span className="text-xs text-amber-300/80 leading-none mb-0.5 font-mono">{tok.pinyin}</span>
                              )}
                              <span className="text-3xl md:text-4xl font-bold text-white leading-snug">{tok.text}</span>
                            </span>
                          ) : (
                            <span key={i} className="text-3xl md:text-4xl font-bold text-white self-end">{tok.text}</span>
                          )
                        ))}
                      </div>
                    ) : (
                      <>
                        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 leading-relaxed">
                          {previewSentences[currentPreviewIndex]?.target}
                        </h1>
                        {previewSentences[currentPreviewIndex]?.transliteration && (
                          <p className="text-base md:text-lg text-amber-300/80 italic mb-6 font-mono">
                            {previewSentences[currentPreviewIndex].transliteration}
                          </p>
                        )}
                      </>
                    )}
                    
                    {/* Audio Button */}
                    <Button
                      variant="outline"
                      size="lg"
                      className={`rounded-full px-8 py-6 transition-all ${
                        previewAudioPlayed 
                          ? 'border-amber-500 text-amber-400 bg-amber-500/10' 
                          : 'border-slate-600 text-slate-400 hover:border-amber-500'
                      }`}
                      onClick={playPreviewAudio}
                    >
                      <Volume2 className="h-6 w-6 mr-2" />
                      {previewAudioPlayed ? t('reader.playAudio') : t('reader.listen')}
                    </Button>
                  </div>
                  
                  {/* Divider */}
                  <div className="flex items-center justify-center my-6">
                    <div className="flex-1 h-px bg-slate-700" />
                    <ArrowRight className="h-5 w-5 mx-4 text-slate-500" />
                    <div className="flex-1 h-px bg-slate-700" />
                  </div>
                  
                  {/* English Translation - Revealed after audio or click */}
                  <div className="text-center">
                    <p className="text-sm text-slate-400 uppercase tracking-wider mb-4">
                      {t('forge.englishMeaning')}
                    </p>
                    {showTranslation ? (
                      <h2 className="text-2xl md:text-3xl font-semibold text-emerald-400 animate-fadeIn leading-relaxed">
                        {previewSentences[currentPreviewIndex]?.english}
                      </h2>
                    ) : (
                      <button 
                        onClick={() => setShowTranslation(true)}
                        className="h-16 flex items-center justify-center w-full hover:bg-slate-700/30 rounded-lg transition-colors cursor-pointer"
                      >
                        <p className="text-slate-500 text-lg">
                          {previewAudioPlayed ? t('forge.revealTranslation') : t('forge.listenFirst')}
                        </p>
                      </button>
                    )}
                  </div>
                  
                  {/* Tip for keyboard users */}
                  <div className="text-center mt-6 pt-4 border-t border-slate-700">
                    <p className="text-xs text-slate-500">
                      <kbd className="px-2 py-1 bg-slate-700 rounded text-slate-400 text-xs">Space</kbd> reveal • 
                      <kbd className="px-2 py-1 bg-slate-700 rounded text-slate-400 text-xs ml-2">Enter</kbd> next
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Next Button */}
              <Button
                className="mt-8 w-full max-w-md bg-amber-600 hover:bg-amber-700 text-xl py-7"
                onClick={nextPreviewSentence}
                disabled={!showTranslation}
                data-testid="next-preview-btn"
              >
                {currentPreviewIndex < previewSentences.length - 1 ? (
                  <>{t('forge.nextSentence')} <ArrowRight className="ml-2 h-6 w-6" /></>
                ) : (
                  <>{t('forge.startQuestions')} <ArrowRight className="ml-2 h-6 w-6" /></>
                )}
              </Button>
            </>
          ) : (
            <div className="text-center">
              <p className="text-slate-400 mb-4">{t('forge.failedToLoad')}</p>
              <Button onClick={skipPreview} className="bg-amber-600 hover:bg-amber-700">
                {t('forge.startQuestions')} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          )}
        </div>

        {/* Animation styles */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.5s ease-out forwards;
          }
        `}</style>
      </div>
    );
  }

  // Step Stats Screen
  if (showStepStats) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        <Card className="w-full max-w-lg text-center bg-slate-900 border-amber-500/30">
          <CardContent className="pt-8 pb-6">
            <div className={`w-20 h-20 rounded-full ${levelColor.bg} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
              <Target className={`h-10 w-10 ${levelColor.text}`} />
            </div>
            
            <h2 className="text-2xl font-bold mb-1 text-white capitalize">
              {t('common.done')}!
            </h2>
            <p className="text-slate-400 mb-6">
              {t(`forge.${currentLevel}`)} {t('forge.levelLabel')} • {t('forge.lesson')} {lesson}
            </p>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <Check className="h-6 w-6 text-green-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-green-400">{stepStats.correct}</p>
                <p className="text-xs text-green-300">{t('forge.correct')}</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <X className="h-6 w-6 text-red-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-red-400">{stepStats.wrong}</p>
                <p className="text-xs text-red-300">{t('forge.incorrect')}</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <Zap className="h-6 w-6 text-amber-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-amber-400">{stepStats.score}</p>
                <p className="text-xs text-amber-300">{t('forge.xp')}</p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <Clock className="h-6 w-6 text-blue-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-blue-400">{stepStats.totalTime}s</p>
                <p className="text-xs text-blue-300">{t('forge.timeSpent')}</p>
              </div>
            </div>
            
            {/* Perfect Bonus */}
            {stepStats.perfectBonus > 0 && (
              <div className="mb-6 p-3 bg-yellow-500/20 rounded-xl border border-yellow-500/30 animate-pulse">
                <Award className="h-8 w-8 text-yellow-400 mx-auto mb-1" />
                <p className="font-bold text-yellow-300">{t('forge.correct')}</p>
                <p className="text-sm text-yellow-200">+{stepStats.perfectBonus} Bonus</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate(`/forge/${languageCode}?course=${course}`)}
                className="border-slate-600"
              >
                {t('forge.backToPath')}
              </Button>
              <Button 
                onClick={completeStep}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Level/Lesson complete screens
  if (lessonComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-slate-900 via-amber-900/20 to-slate-900">
        <Card className="w-full max-w-md text-center bg-slate-900 border-amber-500/30">
          <CardContent className="pt-8 pb-6">
            <Trophy className="h-24 w-24 text-yellow-500 mx-auto mb-4 animate-bounce" />
            <h2 className="text-3xl font-bold mb-2 text-white">{t('forge.lessonComplete')}</h2>
            <p className="text-slate-400 mb-6">
              {t('forge.masteredLesson')} {lesson}!
            </p>
            
            {rankUp && (
              <div className="mb-6 p-4 bg-amber-500/20 rounded-xl border border-amber-500/30">
                <Sparkles className="h-10 w-10 text-amber-400 mx-auto mb-2" />
                <p className="font-bold text-xl text-amber-300">Rank Up!</p>
                <p className="text-amber-100">{rankUp.rank.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                {rankUp.image && (
                  <img src={rankUp.image} alt="New Rank" className="h-40 mx-auto mt-4" />
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate(`/forge/${languageCode}?course=${course}`)}
                className="border-slate-600"
              >
                {t('forge.backToPath')}
              </Button>
              <Button 
                onClick={goToNextLesson}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {t('forge.nextLesson')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            
            {/* Grammar Recommendations after Forge lesson */}
            <div className="mt-6">
              <GrammarRecommendations 
                type="after-forge" 
                languageCode={languageCode}
                lessonNumber={parseInt(lesson)}
                limit={2}
                showDismiss={true}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (levelComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-slate-900 to-slate-800">
        <Card className="w-full max-w-md text-center bg-slate-900 border-slate-700">
          <CardContent className="pt-8 pb-6">
            <div className={`w-24 h-24 rounded-full ${levelColor.bg} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
              <Star className={`h-12 w-12 ${levelColor.text}`} />
            </div>
            <h2 className="text-2xl font-bold mb-2 capitalize text-white">{t(`forge.${currentLevel}`)} {t('forge.levelLabel')} {t('forge.completed')}!</h2>
            <p className="text-slate-400 mb-6">
              {t('forge.xp')}: {score}
            </p>
            
            {rankUp && (
              <div className="mb-6 p-4 bg-amber-500/20 rounded-xl border border-amber-500/30">
                <Sparkles className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                <p className="font-bold text-amber-300">Rank Up!</p>
                <p className="text-sm text-amber-100">{rankUp.rank}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate(`/forge/${languageCode}?course=${course}`)}
                className="border-slate-600"
              >
                Back to Path
              </Button>
              <Button 
                onClick={continueToNextLevel}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {currentLevel === 'gold' ? 'Complete' : 'Next Level'} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800" data-testid="forge-lesson">
      {/* Header */}
      <div className={`sticky top-0 z-10 ${levelColor.bg} ${levelColor.border} border-b p-3`}>
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <Button 
            variant="ghost" 
            size="sm"
            className={levelColor.text}
            onClick={() => navigate(`/forge/${languageCode}?course=${course}`)}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <div className="text-center">
            <p className={`text-sm font-bold capitalize ${levelColor.text}`}>
              {retryPhase ? '🔄 Review Mode' : `${currentLevel} Level • Step ${currentStep}/4`}
            </p>
            <p className={`text-xs ${levelColor.accent} opacity-80`}>
              Lesson {lesson} • {lessonData?.theme}
            </p>
          </div>
          
          <div className={`text-right ${levelColor.text}`}>
            <p className="text-lg font-bold">{score}</p>
            <p className="text-xs opacity-75">pts</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="max-w-5xl mx-auto mt-2">
          <Progress value={progressPercent} className="h-2" />
          <div className="flex justify-between text-xs mt-1 opacity-75">
            <span>{currentIdx + 1} / {totalQuestions}</span>
            {streak >= 3 && (
              <span className="text-amber-300 flex items-center gap-1">
                <Flame className="h-3 w-3" /> {streak} streak!
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area - Optimized for mobile */}
      <div className="max-w-5xl mx-auto px-2 py-1 md:p-4 flex flex-col md:flex-row gap-2 md:gap-6 min-h-[calc(100vh-100px)] md:min-h-0">
        {/* Blacksmith Card - Hidden on mobile during questions */}
        <div className="hidden md:block w-full md:w-80 flex-shrink-0">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6 text-center">
              {blacksmith && (
                <>
                  <div className={`relative transition-transform duration-300 ${
                    smithAnimating ? (smithReaction === 'correct' ? 'scale-110' : 'shake') : ''
                  }`}>
                    {/* TRIPLED IMAGE SIZE: from h-32 to h-96 */}
                    <img 
                      src={blacksmith.blacksmith_image} 
                      alt={blacksmith.blacksmith_rank_display}
                      className="h-72 w-72 md:h-80 md:w-80 mx-auto object-contain"
                    />
                    {/* Hammer animation on correct */}
                    {smithAnimating && smithReaction === 'correct' && (
                      <div className="absolute top-4 right-4 animate-hammer">
                        <Hammer className="h-12 w-12 text-amber-400" />
                      </div>
                    )}
                    {/* Sparks on correct */}
                    {smithAnimating && smithReaction === 'correct' && (
                      <div className="absolute inset-0 pointer-events-none">
                        {[...Array(8)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute w-2 h-2 bg-amber-400 rounded-full animate-spark"
                            style={{
                              left: '50%',
                              top: '50%',
                              animationDelay: `${i * 0.08}s`,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <p className="text-amber-400 font-semibold text-lg mt-4">
                    {blacksmith.blacksmith_rank_display}
                  </p>
                  
                  {/* Total Score Display */}
                  <div className="mt-3 p-3 bg-slate-900/50 rounded-lg border border-amber-500/30">
                    <p className="text-3xl font-bold text-amber-400">{score}</p>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Total Score</p>
                  </div>
                  
                  {/* Speech bubble - larger */}
                  <div className={`mt-4 p-4 rounded-xl text-base font-medium transition-all duration-300 ${
                    smithReaction === 'correct' 
                      ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                      : smithReaction === 'wrong'
                        ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                        : 'bg-slate-700/50 text-slate-300'
                  }`}>
                    {smithMessage}
                  </div>
                  
                  {/* Level indicator */}
                  <div className="mt-4 flex justify-center gap-2">
                    {['iron', 'bronze', 'silver', 'gold'].map((lvl) => (
                      <div
                        key={lvl}
                        className={`w-3 h-3 rounded-full ${
                          lvl === currentLevel ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-800' : ''
                        } ${
                          lvl === 'iron' ? 'bg-slate-500' :
                          lvl === 'bronze' ? 'bg-amber-700' :
                          lvl === 'silver' ? 'bg-slate-300' :
                          'bg-yellow-400'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Question Area - Full screen on mobile, optimized spacing */}
        <div className="flex-1 flex flex-col min-h-0">
          {currentQuestion ? (
            <Card className="bg-slate-800/50 border-slate-700 flex-1 flex flex-col overflow-hidden">
              <CardContent className="pt-3 md:pt-8 pb-3 md:pb-8 px-3 md:px-6 flex-1 flex flex-col justify-between overflow-y-auto">
                {/* Retry phase indicator */}
                {retryPhase && (
                  <div className="mb-2 md:mb-4 p-2 bg-amber-500/20 rounded-lg border border-amber-500/30 text-center">
                    <p className="text-amber-300 text-xs md:text-sm">Review: Let's try this one again!</p>
                  </div>
                )}
                
                {/* Question prompt - Compact on mobile, better wrapping */}
                <div className="mb-3 md:mb-8 flex-shrink-0">
                  <p className="text-xs md:text-lg text-amber-400 text-center mb-1">{t('forge.translate')}</p>
                  <p className="text-lg md:text-3xl font-medium text-center text-white leading-tight md:leading-relaxed break-words">
                    {currentQuestion.prompt}
                  </p>
                  
                  {/* Audio play button - smaller on mobile */}
                  {currentQuestion.word_id && (
                    <div className="flex justify-center mt-2 md:mt-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10 h-8 md:h-10"
                        onClick={() => playWordAudio(currentQuestion.word_id)}
                        data-testid="play-audio-btn"
                      >
                        <Volume2 className="h-4 w-4 md:h-6 md:w-6 mr-1 md:mr-2" />
                        <span className="text-xs md:text-base">Play</span>
                      </Button>
                    </div>
                  )}
                  
                  {currentQuestion.sentence && (
                    <p className="text-slate-400 text-center mt-2 italic text-xs md:text-lg break-words">
                      "{currentQuestion.sentence}"
                    </p>
                  )}
                </div>

                {/* Answer area - Compact on mobile, better fit */}
                <div className="flex-1 flex flex-col justify-center min-h-0 overflow-y-auto">
                {currentQuestion.question_type === 'multiple_choice' && (
                  <div className="grid grid-cols-1 gap-1.5 md:gap-4">
                    {currentQuestion.options?.map((option, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        className={`w-full justify-start text-left h-auto py-2 md:py-5 px-2 md:px-5 border-2 transition-all text-xs md:text-lg ${
                          showFeedback
                            ? option === currentQuestion.correct_answer
                              ? 'bg-green-500/20 border-green-500 text-green-300'
                              : option === selectedAnswer
                                ? 'bg-red-500/20 border-red-500 text-red-300'
                                : 'border-slate-600 text-slate-400'
                            : selectedAnswer === option
                              ? 'border-amber-500 bg-amber-500/10'
                              : 'border-slate-600 hover:border-slate-500 text-slate-200'
                        }`}
                        onClick={() => !showFeedback && submitAnswer(option)}
                        disabled={showFeedback}
                        data-testid={`option-${idx}`}
                      >
                        <span className={`mr-2 md:mr-4 w-6 h-6 md:w-9 md:h-9 rounded-full flex items-center justify-center text-xs md:text-base font-bold flex-shrink-0 ${
                          showFeedback && option === currentQuestion.correct_answer
                            ? 'bg-green-500 text-white'
                            : showFeedback && option === selectedAnswer
                              ? 'bg-red-500 text-white'
                              : 'bg-slate-700 text-slate-300'
                        }`}>
                          {String.fromCharCode(65 + idx)}
                        </span>
                        <span className="flex-1 break-words text-left">{option}</span>
                        {showFeedback && option === currentQuestion.correct_answer && (
                          <Check className="h-5 w-5 md:h-6 md:w-6 text-green-400 flex-shrink-0" />
                        )}
                        {showFeedback && option === selectedAnswer && option !== currentQuestion.correct_answer && (
                          <X className="h-5 w-5 md:h-6 md:w-6 text-red-400 flex-shrink-0" />
                        )}
                      </Button>
                    ))}
                  </div>
                )}

                {currentQuestion.question_type === 'typing' && (
                  <div className="space-y-3 md:space-y-5">
                    <Input
                      ref={typingInputRef}
                      value={typedAnswer}
                      onChange={(e) => setTypedAnswer(e.target.value)}
                      placeholder={t('forge.typeAnswer')}
                      className="text-center text-lg md:text-xl h-12 md:h-14 bg-slate-700 border-slate-600 text-white"
                      disabled={showFeedback}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && typedAnswer.trim()) {
                          submitAnswer(typedAnswer.trim());
                        }
                      }}
                      data-testid="typing-input"
                    />
                    
                    {/* Special Characters Keyboard Toggle */}
                    {!showFeedback && (
                      <div className="flex justify-center">
                        <SpecialCharactersKeyboard
                          onCharacterClick={handleSpecialCharacterClick}
                          isOpen={showSpecialKeyboard}
                          onToggle={() => setShowSpecialKeyboard(!showSpecialKeyboard)}
                        />
                      </div>
                    )}
                    
                    {showFeedback && (
                      <div className={`text-center p-4 rounded-lg text-lg ${isCorrect ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                        <p className={isCorrect ? 'text-green-400' : 'text-red-400'}>
                          {isCorrect ? t('forge.correct') : `${t('forge.showAnswer')}: ${currentQuestion.correct_answer}`}
                        </p>
                      </div>
                    )}
                    
                    {!showFeedback && (
                      <Button 
                        className="w-full bg-amber-600 hover:bg-amber-700 h-14 text-lg" 
                        onClick={() => submitAnswer(typedAnswer.trim())}
                        disabled={!typedAnswer.trim()}
                      >
                        {t('forge.checkAnswer')}
                      </Button>
                    )}
                  </div>
                )}

                {currentQuestion.question_type === 'drag_drop' && (
                  <div className="space-y-5">
                    <p className="text-base text-slate-400 text-center">
                      Tap words in the correct order
                    </p>
                    
                    {/* Selected words area */}
                    <div className="min-h-[80px] p-4 border-2 border-dashed border-slate-600 rounded-lg flex flex-wrap gap-3 bg-slate-800/50">
                      {dragItems.filter(item => item.selected).map((item, idx) => (
                        <Badge 
                          key={idx}
                          variant="secondary"
                          className="cursor-pointer bg-amber-600 hover:bg-amber-700 text-white text-base px-4 py-2"
                          onClick={() => {
                            if (!showFeedback) {
                              setDragItems(prev => 
                                prev.map(i => i === item ? {...i, selected: false} : i)
                              );
                            }
                          }}
                        >
                          {item.word}
                        </Badge>
                      ))}
                    </div>
                    
                    {/* Available words */}
                    <div className="flex flex-wrap gap-3 justify-center">
                      {dragItems.filter(item => !item.selected).map((item, idx) => (
                        <Badge 
                          key={idx}
                          variant="outline"
                          className="cursor-pointer border-slate-500 text-slate-300 hover:bg-slate-700 text-base px-4 py-2"
                          onClick={() => {
                            if (!showFeedback) {
                              setDragItems(prev => 
                                prev.map(i => i === item ? {...i, selected: true} : i)
                              );
                            }
                          }}
                        >
                          {item.word}
                        </Badge>
                      ))}
                    </div>

                    {showFeedback && (
                      <div className={`text-center p-4 rounded-lg text-lg ${isCorrect ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                        <p className={isCorrect ? 'text-green-400' : 'text-red-400'}>
                          {isCorrect ? 'Correct!' : `Correct: ${currentQuestion.correct_answer}`}
                        </p>
                      </div>
                    )}
                    
                    {!showFeedback && (
                      <Button 
                        className="w-full bg-amber-600 hover:bg-amber-700 h-14 text-lg" 
                        onClick={() => {
                          const answer = dragItems
                            .filter(i => i.selected)
                            .map(i => i.word)
                            .join(' ');
                          submitAnswer(answer);
                        }}
                      >
                        Check Answer
                      </Button>
                    )}
                  </div>
                )}

                {currentQuestion.question_type === 'speaking' && (
                  <div className="space-y-5 text-center">
                    {/* Text to speak */}
                    <div className="p-6 bg-slate-700 rounded-xl">
                      <p className="text-slate-400 text-sm mb-2">Say this in {languageCode.toUpperCase()}:</p>
                      <p className="text-3xl font-bold text-white mb-3">
                        {currentQuestion.text_to_speak || currentQuestion.correct_answer}
                      </p>
                      {/* Play audio button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-slate-500"
                        onClick={() => {
                          const text = currentQuestion.text_to_speak || currentQuestion.correct_answer;
                          if ('speechSynthesis' in window) {
                            const utterance = new SpeechSynthesisUtterance(text);
                            const langMap = {
                              'ru': 'ru-RU', 'zh': 'zh-CN', 'fr': 'fr-FR', 'de': 'de-DE',
                              'es': 'es-ES', 'no': 'nb-NO', 'ja': 'ja-JP', 'ko': 'ko-KR',
                              'ar': 'ar-SA', 'fa': 'fa-IR', 'it': 'it-IT', 'pt': 'pt-BR',
                            };
                            utterance.lang = langMap[languageCode] || languageCode;
                            utterance.rate = 0.8;
                            window.speechSynthesis.speak(utterance);
                          }
                        }}
                      >
                        <Volume2 className="h-4 w-4 mr-2" />
                        Listen
                      </Button>
                    </div>
                    
                    {/* Spoken text display */}
                    {spokenText && (
                      <div className="p-3 bg-slate-800 rounded-lg border border-slate-600">
                        <p className="text-slate-400 text-xs mb-1">You said:</p>
                        <p className="text-white text-lg">{spokenText}</p>
                      </div>
                    )}
                    
                    {/* Error display */}
                    {speechError && (
                      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-red-400 text-sm">{speechError}</p>
                      </div>
                    )}
                    
                    {/* Recording button */}
                    {!showFeedback && (
                      <div className="flex flex-col items-center gap-3">
                        <Button 
                          size="lg" 
                          className={`rounded-full w-24 h-24 transition-all ${
                            isRecording 
                              ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                              : 'bg-amber-600 hover:bg-amber-700'
                          }`}
                          onClick={isRecording ? stopSpeechRecognition : startSpeechRecognition}
                        >
                          <Mic className={`h-10 w-10 ${isRecording ? 'animate-pulse' : ''}`} />
                        </Button>
                        <p className="text-base text-slate-400">
                          {isRecording ? 'Listening... Tap to stop' : 'Tap to speak'}
                        </p>
                        
                        {/* Skip speaking option */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-500 hover:text-slate-300"
                          onClick={() => submitAnswer(currentQuestion.correct_answer)}
                        >
                          Can't use microphone? Skip
                        </Button>
                      </div>
                    )}
                    
                    {/* Feedback for speaking */}
                    {showFeedback && (
                      <div className={`p-4 rounded-lg ${isCorrect ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                        <p className={`text-lg font-semibold ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                          {isCorrect ? 'Great pronunciation!' : 'Not quite right'}
                        </p>
                        {!isCorrect && (
                          <div className="mt-2">
                            <p className="text-slate-400 text-sm">Correct pronunciation:</p>
                            <p className="text-amber-400 text-lg">{currentQuestion.correct_answer}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Feedback and Next button */}
                {showFeedback && (
                  <div className="mt-4 md:mt-8 flex-shrink-0 space-y-3">
                    {/* Suggest different answer button - only show if wrong */}
                    {!isCorrect && (
                      <button
                        onClick={() => {
                          setSuggestionText(typedAnswer || selectedAnswer || '');
                          setShowSuggestionDialog(true);
                        }}
                        className="w-full flex items-center justify-center gap-2 py-2 text-sm text-slate-400 hover:text-amber-400 transition-colors"
                        data-testid="suggest-answer-btn"
                      >
                        <MessageSquarePlus className="w-4 h-4" />
                        Think your answer should be correct? Suggest it!
                      </button>
                    )}
                    <Button 
                      className="w-full bg-amber-600 hover:bg-amber-700 text-lg md:text-xl py-4 md:py-7" 
                      onClick={nextQuestion} 
                      data-testid="next-btn"
                    >
                      Continue <ArrowRight className="ml-2 h-5 w-5 md:h-6 md:w-6" />
                    </Button>
                  </div>
                )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-16">
              <Loader2 className="h-10 w-10 animate-spin mx-auto text-slate-500" />
              <p className="mt-3 text-slate-400 text-lg">Loading questions...</p>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .shake {
          animation: shake 0.3s ease-in-out;
        }
        
        @keyframes hammer {
          0% { transform: rotate(0deg) translateY(0); }
          50% { transform: rotate(-30deg) translateY(-10px); }
          100% { transform: rotate(0deg) translateY(0); }
        }
        .animate-hammer {
          animation: hammer 0.3s ease-in-out;
        }
        
        @keyframes spark {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          100% { 
            transform: translate(
              calc(-50% + ${Math.random() * 60 - 30}px), 
              calc(-50% - ${40 + Math.random() * 30}px)
            ) scale(0);
            opacity: 0;
          }
        }
        .animate-spark {
          animation: spark 0.6s ease-out forwards;
        }
      `}</style>

      {/* AI Question Helper - floating button */}
      <AIQuestionHelper 
        contextType="lesson"
        contextText={currentQuestion ? `${currentQuestion.prompt} - ${currentQuestion.correct_answer}` : (lessonData?.theme || '')}
        language={lessonData?.language_name || languageCode || ''}
      />

      {/* Suggestion Dialog */}
      <Dialog open={showSuggestionDialog} onOpenChange={setShowSuggestionDialog}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-amber-400" />
              Suggest an Alternative Answer
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              If you think your answer should be accepted, let us know! Your suggestion will be reviewed and may be added as an accepted answer.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Current correct answer:</label>
              <div className="p-2 rounded bg-slate-800 text-green-400 font-mono">
                {(retryPhase ? retryQuestions[retryIndex] : questions[currentQuestionIndex])?.correct_answer}
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Your suggested answer:</label>
              <Textarea
                value={suggestionText}
                onChange={(e) => setSuggestionText(e.target.value)}
                placeholder="Enter your suggested answer..."
                className="bg-slate-800 border-slate-700 text-white"
                rows={2}
              />
            </div>
            
            <p className="text-xs text-slate-500">
              Your suggestion will be posted to the Report Issues forum for admin review.
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowSuggestionDialog(false)}
              className="text-slate-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={submitSuggestion}
              disabled={submittingSuggestion || !suggestionText.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submittingSuggestion ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Suggestion'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
